import { FC, ReactNode, useEffect } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Http } from "../../common/http";
import { AppRoutes } from "../../common/routesUtils";
import { buildDynamicLayout } from "../../config/headerPage/headerPageBuild.dynamic";
import { HeaderPageContextMap } from "../../config/headerPage/headerPageMap.dynamic";
import { PAGE_LAYOUT } from "../../config/page.static";
import { PageType } from "../../config/page.types";
import { useBreadcrumbs } from "../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../context/FiltersProvider";
import { hasDashboardFilters } from "../../context/FiltersProvider/selectors/dashboardSelectors";
import { ErrorResponse } from "../../datasources/api";
import { useAuthentication } from "../../hooks/useAuthentication";
import { useNavigationPreferences } from "../../hooks/usePreferences";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../state/view";
import { Dashboard } from "../common/Dashboard";
import ErrorMessage, { ReloadAction } from "../common/ErrorMessage";
import { FilterContexType, ViewHeader } from "./ViewHeader";

interface Props {
	children: ReactNode;
	pb?: string | number;
	height?: string | number;
	sx?: SxProps<Theme>;
	errors?: Array<Error | null>;
	redirectOnForbidden?: boolean;
	redirectOnUnauthorized?: boolean;
	disableCompaniesSelector?: boolean;
	replaceContentWithError?: ReloadAction;

	onAfterDisplayError?: () => void;

	filterContext?: FilterContexType;

	pageType?: PageType;
	headerContext?: HeaderPageContextMap[keyof HeaderPageContextMap];
}

/**
 * View is the top-level component that is used to embed other views and contains all
 * elements that are shared by all views.
 *
 * Views are usually embedded in an AppLayout component (see ./AppLayout.tsx) as embedded
 * components, so that they will share the same top bar and side menu.
 *
 * @param children                 a ReactNode, usually the actual page content
 * @param errors                   the list of errors that the view should manage;
 *                                 the view evaluates all errors and stops at the first non-null
 *                                 value, then it may show a snackbar or redirect to
 *                                 another section (see redirectOn* props)
 * @param pb                       the optional bottom padding of the view
 * @param height                   the custom height of the view, useful for some edge-case like maps
 * @param sx                       optional MUI sx styles applied to the root container
 * @param redirectOnForbidden      if true, if the evaluated error is HTTP 403 the view
 *                                 will redirect users to "Forbidden" section, otherwise
 *                                 it will present a snackbar with the error message (default: false)
 * @param redirectOnUnauthorized   if true, if the evaluated error is HTTP 401 the view
 *                                 will sign out the user and redirect to "Unauthorized" section,
 *                                 otherwise it will present a snackbar (default: true)
 * @param replaceContentWithError  this option determines if errors replace the view content unless
 *                                 the first evaluated error redirects users to another section;
 *                                 in other words, if errors will not cause redirects, the
 *                                 view will replace its content with an error message
 *                                 instead of rendering a temporary snackbar (default: undefined)
 *
 * @param onAfterDisplayError      the optional handler invoked right after publishing an error
 *
 *
 * @param pageType                 identifies the current page;
 *                                 used to:
 *                                 - build and update breadcrumbs via BreadcrumbsProvider
 *                                 - resolve static layout configuration from PAGE_LAYOUT
 *                                 - enable the ViewHeader rendering
 *
 * @param headerContext            optional runtime data used to build dynamic header content
 *                                 its type is enforced by HeaderPageContextMap based on the
 * 								   provided pageType
 *
 * @returns                        the child node view wrapped with shared layout logic,
 *                                 header configuration, filters handling, and error management
 */

const View: FC<Props> = ({
	children,
	height,
	pb,
	errors,
	redirectOnForbidden = false,
	redirectOnUnauthorized = true,
	replaceContentWithError,
	onAfterDisplayError,
	sx,
	pageType,
	headerContext,
}) => {
	const navigateTo = useNavigate();
	const { signOut } = useAuthentication();
	const { t } = useTranslation(["errors", "layoutTitles"]);
	const { breadcrumbs } = useBreadcrumbs();
	const { navigationPreferences } = useNavigationPreferences();

	const publishMessage = useSetAtom(publishSnackbarMessage);

	const { dashboardFilters, advancedMode } = useFiltersRegistry();
	const showDashboard = hasDashboardFilters(dashboardFilters, advancedMode);

	const error = errors?.find((e) => e !== null) as ErrorResponse;

	// Errors management effect.
	useEffect(() => {
		if (!error) {
			return;
		}

		// Check if the error cause is "Unauthorized" (HTTP 401).
		if (error.httpCode === Http.Unauthorized && redirectOnUnauthorized) {
			signOut();
			navigateTo(AppRoutes.Unauthorized);
			return;
		}

		// Check if the error cause is "Forbidden" (HTTP 403).
		if (error.httpCode === Http.Forbidden && redirectOnForbidden) {
			navigateTo(AppRoutes.Forbidden);
			return;
		}

		// Check if the error should be presented as a temporary message
		// or if it should replace the view content.
		if (!replaceContentWithError) {
			// Otherwise publish an error message.
			const message = t(error.message);
			publishMessage(makeSnackbarMessage(message, "error"));
			onAfterDisplayError?.();
		}
	}, [
		error,
		publishMessage,
		navigateTo,
		signOut,
		onAfterDisplayError,
		t,
		redirectOnForbidden,
		redirectOnUnauthorized,
		replaceContentWithError,
	]);

	const viewWithoutFilterMenu = [
		PageType.Account,
		PageType.PlatformSettingsAdmin,
		PageType.ResultsTransportGreenRouterDetail,
		PageType.ResultsTransportThirdPartyDetail,
		PageType.ResultsOperationalUnitsGreenRouterDetail,
		PageType.ResultsOperationalUnitsThirdPartyDetail,
		PageType.DataInsightsRootCause,
		PageType.DataInsightsRootCauseResults,
		PageType.MasterDataOperationalUnitsAddUnit,
		PageType.MasterDataOperationalUnitsDetail,
		PageType.DataInsightsDashboard,
		PageType.DataInsightsDashboardDetail,
		PageType.ResultsTransportGreenRouterEntityDetail,
		PageType.WhatIfCarbonSnap,
	];

	const hideMenuFilterFromView = viewWithoutFilterMenu.includes(breadcrumbs.at(-1)?.pageType as PageType);

	const viewWithoutLogicalOperator = [
		PageType.PlatformSettingsAdmin,
		PageType.ResultsTransportGreenRouterDetail,
		PageType.ResultsTransportThirdParty,
		PageType.ResultsTransportThirdPartyDetail,
		PageType.ResultsOperationalUnits,
		PageType.ResultsOperationalUnitsGreenRouter,
		PageType.ResultsOperationalUnitsGreenRouterDetail,
		PageType.ResultsOperationalUnitsThirdParty,
		PageType.ResultsOperationalUnitsThirdPartyDetail,
		PageType.DataInsightsRootCause,
		PageType.DataInsightsRootCauseResults,
	];

	const { visitPage } = useBreadcrumbs();

	const staticLayout = pageType ? PAGE_LAYOUT[pageType] : undefined;

	const baseFilterContext: FilterContexType = staticLayout?.filterContext ?? [];

	// Entities can be added only if the page can have operator logic
	// Only if the view can have trips for filter context
	// and the operator should be operational
	// else the filet context just remain trips
	const shouldAddEntities =
		pageType &&
		!viewWithoutLogicalOperator.includes(pageType) &&
		navigationPreferences.transportsPerspective !== "operational";

	const filterContext: FilterContexType =
		shouldAddEntities && !baseFilterContext.includes("entities")
			? [...baseFilterContext, "entities"]
			: baseFilterContext;

	useEffect(() => {
		if (pageType && !breadcrumbs.length) {
			visitPage(pageType);
		}
	}, [pageType, visitPage, breadcrumbs.length]);

	// Reset scroll when page changes
	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "auto",
		});
	}, [pageType]);

	const dynamicLayout = pageType
		? buildDynamicLayout(pageType as keyof HeaderPageContextMap, headerContext)
		: undefined;

	return (
		<Box component="main" sx={{ flexGrow: 1, p: 3, pt: 10, height, pb, ...sx }}>
			{error && replaceContentWithError && (
				<ErrorMessage httpStatusCode={error.httpCode} actions={[replaceContentWithError]} />
			)}

			{pageType && (
				<ViewHeader
					filterContext={filterContext}
					hideMenuFilterFromView={hideMenuFilterFromView}
					dynamicLayout={dynamicLayout}
					title={staticLayout?.title && t(`layoutTitles:titles/${staticLayout.title}`)}
					subtitle={staticLayout?.subtitle && t(`layoutTitles:subtitles/${staticLayout.subtitle}`)}
				/>
			)}

			{showDashboard && !hideMenuFilterFromView && <Dashboard filterContext={filterContext} />}

			{(!replaceContentWithError || !error) && children}
		</Box>
	);
};

export { View };
